import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {DateTime} from 'luxon';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {conditionOutputSchema} from '../../promptToCardAgentV2.schemas';
import {MacroItemTypeValue} from 'common/src/modules/macros/macros.types';
import validateConditionOutput, {
  validateStrictOptionalTextValuesForSignal,
} from '../../utils/conditions/validateConditionOutput';
import {ConditionCardScratchpadState} from '../../scratchpad/conditionCardScratchpad.types';
import {ConditionOperatorKey} from 'common/src/modules/conditions/conditions.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {NodeType} from 'common/src/modules/nodes/nodes.types';

const editConditionOutputToolSchema = z.object({
  outputId: z.number().describe('The id of the output to update'),
  conditionOutput: conditionOutputSchema.extend({
    name: z.string().describe('The name of the output'),
  }),
});

export default function editConditionOutputTool() {
  async function execute(
    params: ToolExecuteParams<typeof editConditionOutputToolSchema, ConditionCardScratchpadState>,
  ) {
    const {outputId, conditionOutput} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<ConditionCardScratchpadState>(
      agentScratchpad,
      NodeType.CONDITION,
    );

    const currentOutput = validatedScratchpad.card.outputs.find((output) => output.id === outputId);

    if (!currentOutput) {
      return toolError(
        `Condition output ID ${outputId} was not found for card ID ${validatedScratchpad.card.id}.`,
      );
    }

    const error = validateConditionOutput(
      conditionOutput,
      validatedScratchpad.card.condKeyType as MacroItemTypeValue,
      validatedScratchpad.card.condOperator as ConditionOperatorKey,
    );

    if (error) {
      return toolError(error);
    }

    const strictOptionalError = validateStrictOptionalTextValuesForSignal(
      validatedScratchpad.card.condKey,
      validatedScratchpad.card.availableSignals,
      conditionOutput.textValues,
    );

    if (strictOptionalError) {
      return toolError(strictOptionalError);
    }

    currentOutput.name = conditionOutput.name;

    if (conditionOutput.textValues) currentOutput.textValues = conditionOutput.textValues;
    if (conditionOutput.numericValues) currentOutput.numericValues = conditionOutput.numericValues;
    if (conditionOutput.booleanValue) currentOutput.booleanValue = conditionOutput.booleanValue;
    if (conditionOutput.dateValues)
      currentOutput.dateValues = conditionOutput.dateValues.map((date) => DateTime.fromISO(date));
    if (conditionOutput.datesRange)
      currentOutput.datesRange = {
        from: DateTime.fromISO(conditionOutput.datesRange.from),
        to: DateTime.fromISO(conditionOutput.datesRange.to),
      };
    if (conditionOutput.numbersRange)
      currentOutput.numbersRange = {
        from: conditionOutput.numbersRange.from,
        to: conditionOutput.numbersRange.to,
      };

    return toolResult('Condition output updated');
  }

  return tool<typeof editConditionOutputToolSchema, ConditionCardScratchpadState>({
    name: V2CardAgentToolName.UpdateConditionOutput,
    displayName: 'Updating condition output',
    description: 'Update a condition output in an existing condition card',
    inputSchema: editConditionOutputToolSchema,
    execute,
  });
}
